import { LostCargoCommand } from '../../services/command';
import {
    GetLostPackageShipmentsRequest,
    LostPackageShipment,
} from '../../types/models/lostCargo/lostCargo.types';
import TimeHelper from '../../utils/time';
import { ShippingListStatus } from './helper/enums';

export const MAX_SHIPMENT_NUMBERS = 1000;
export const LOST_PACKAGE_OVERDUE_DAYS = 21;

const toLocalDateOnly = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const formatLocalDateToApiString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const parseApiDateString = (apiDate: string): Date | null => {
    if (!apiDate) {
        return null;
    }
    const [year, month, day] = apiDate.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
        return null;
    }
    return new Date(year, month - 1, day);
};

export const getCalendarDaysBetween = (earlier: Date, later: Date): number => {
    const start = toLocalDateOnly(earlier);
    const end = toLocalDateOnly(later);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

export const getMaxSelectableDate = (): string => formatLocalDateToApiString(toLocalDateOnly(new Date()));

export const MAX_LOST_CARGO_DATE_RANGE_DAYS = 365;

export const addCalendarDaysToApiString = (apiDate: string, days: number): string | null => {
    const parsed = parseApiDateString(apiDate);
    if (!parsed) {
        return null;
    }
    const shifted = new Date(parsed);
    shifted.setDate(shifted.getDate() + days);
    return formatLocalDateToApiString(shifted);
};

export const isLostCargoStartBeforeEnd = (startDate: string, endDate: string): boolean => {
    const start = parseApiDateString(startDate);
    const end = parseApiDateString(endDate);
    if (!start || !end) {
        return true;
    }
    return getCalendarDaysBetween(start, end) > 0;
};

export const getLostCargoStartDateMax = (endDate: string | undefined): string | undefined => {
    const todayMax = getMaxSelectableDate();
    if (!endDate) {
        return todayMax;
    }
    const maxFromEnd = addCalendarDaysToApiString(endDate, -1);
    if (!maxFromEnd) {
        return todayMax;
    }
    return maxFromEnd < todayMax ? maxFromEnd : todayMax;
};

export const getLostCargoEndDateMin = (startDate: string | undefined): string | undefined => {
    if (!startDate) {
        return undefined;
    }
    return addCalendarDaysToApiString(startDate, 1) ?? undefined;
};

const formatApiDateTime = (date: string): string | null => {
    if (!date) {
        return null;
    }

    return `${TimeHelper.formatDateDesc(date)}T00:00:00`;
};

export const buildLostPackageShipmentsRequest = ({
    startDate,
    endDate,
    customerInvoiceNumber,
    cargoCompanyInvoiceNumber,
    customerShipmentNumbers,
    cargoCompanyShipmentNumbers,
    customerCargoCompanyId,
    cargoCompanyShipmentState,
    onlyTwentyOneDaysOrMoreLostPackages,
    skip,
    take,
}: {
    startDate?: string;
    endDate?: string;
    customerInvoiceNumber?: string;
    cargoCompanyInvoiceNumber?: string;
    customerShipmentNumbers: string[];
    cargoCompanyShipmentNumbers: string[];
    customerCargoCompanyId?: number[];
    cargoCompanyShipmentState?: number[];
    onlyTwentyOneDaysOrMoreLostPackages?: boolean;
    skip: number;
    take: number;
}): GetLostPackageShipmentsRequest => ({
    isLostPackage: false,
    isPartialLostPackage: false,
    customerInvoiceNumber: customerInvoiceNumber || undefined,
    cargoCompanyInvoiceNumber: cargoCompanyInvoiceNumber || undefined,
    customerShipmentNumbers: customerShipmentNumbers.length > 0 ? customerShipmentNumbers : undefined,
    cargoCompanyShipmentNumbers: cargoCompanyShipmentNumbers.length > 0 ? cargoCompanyShipmentNumbers : undefined,
    startDate: formatApiDateTime(startDate || '') || undefined,
    endDate: formatApiDateTime(endDate || '') || undefined,
    customerCargoCompanyId: customerCargoCompanyId?.length ? customerCargoCompanyId : undefined,
    cargoCompanyShipmentState: cargoCompanyShipmentState?.length ? cargoCompanyShipmentState : undefined,
    onlyTwentyOneDaysOrMoreLostPackages: onlyTwentyOneDaysOrMoreLostPackages ?? false,
    skip,
    take,
});

export const buildLostCargoPrintParams = (rowData: any) => ({
    CustomerId: rowData.customerId,
    OrderId: rowData.cargoCompanyOrderId,
    ShippingListId: rowData.cargoCompanyShipmentId,
    NewStatus: ShippingListStatus.INVOICED_TO_CARGO_COMPANY,
    OldStatus: rowData.cargoCompanyShipmentState,
});

export const executeLostCargoPrintRequests = async (selectedRows: any[]) => {
    const printRequests = selectedRows.map((rowData) =>
        LostCargoCommand.PrintOrCancel(buildLostCargoPrintParams(rowData))
            .then(() => null)
            .catch(() => rowData.cargoCompanyShipmentId)
    );

    return Promise.all(printRequests);
};

export const parseShipmentNumbers = (raw: string): string[] => {
    if (!raw?.trim()) {
        return [];
    }

    return raw
        .split(/[\n,;]+/)
        .map((item) => item.trim())
        .filter(Boolean);
};

export const sortByLostPackageDateAsc = (rows: LostPackageShipment[]) =>
    [...(rows || [])].sort((a, b) => {
        const aTime = a?.customerLostPackageDate ? new Date(a.customerLostPackageDate).getTime() : 0;
        const bTime = b?.customerLostPackageDate ? new Date(b.customerLostPackageDate).getTime() : 0;
        return aTime - bTime;
    });

export const resolveNotFoundShipmentNumbers = (
    requestedCargoNumbers: string[],
    requestedCustomerNumbers: string[],
    shipments: LostPackageShipment[]
) => {
    const foundCargo = new Set((shipments || []).map((item) => item?.cargoCompanyShipmentNumber).filter(Boolean));
    const foundCustomer = new Set((shipments || []).map((item) => item?.customerShipmentNumber).filter(Boolean));

    const notFoundCargo = requestedCargoNumbers.filter((number) => !foundCargo.has(number));
    const notFoundCustomer = requestedCustomerNumbers.filter((number) => !foundCustomer.has(number));

    return [...new Set([...notFoundCargo, ...notFoundCustomer])];
};
